import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { WHY_ITEMS } from '../../mocks/sharedData';

interface WhyItem {
  icon: string;
  title: string;
  description: string;
}

interface WhyChooseUsProps {
  eyebrow?: string;
  headline?: string;
  items?: WhyItem[];
}

const WhyChooseUs = ({
  eyebrow = 'Our Difference',
  headline = 'Why Kentuckiana Chooses Star Solar',
  items = WHY_ITEMS,
}: WhyChooseUsProps) => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="bg-stone-50 py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">

        {/* Section label row */}
        <div
          ref={ref}
          className={`flex items-center gap-3 mb-6 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="w-10 h-px bg-amber-500" />
          <span className="text-amber-600 text-xs font-semibold tracking-[0.25em] uppercase">{eyebrow}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left — Headline + layered images */}
          <div
            className={`transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <h2 className="font-serif text-5xl md:text-6xl text-zinc-900 leading-tight mb-10">
              {headline}
            </h2>

            {/* Stacked / offset image layout */}
            <div className="relative">
              {/* Main image */}
              <div className="rounded-3xl overflow-hidden h-80 w-full">
                <img
                  src="https://public.readdy.ai/ai/img_res/6bbfd6dfd27aac060ba3ceb84e718aa9.jpg"
                  alt="Happy homeowners with solar"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/30 to-transparent rounded-3xl" />
              </div>

              {/* Floating stat card — bottom-right offset */}
              <div className="absolute -bottom-6 -right-4 bg-amber-500 rounded-2xl p-5 text-zinc-950">
                <p className="font-serif text-3xl font-bold leading-none mb-1">30%</p>
                <p className="text-xs font-semibold tracking-wide leading-tight">Federal Tax<br />Credit Available</p>
              </div>

              {/* Floating badge — top-left */}
              <div className="absolute -top-4 -left-4 bg-white rounded-2xl shadow-sm p-4 flex items-center gap-3 border border-stone-100">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl">
                  <i className="ri-shield-check-fill text-amber-600 text-xl" />
                </div>
                <div>
                  <p className="text-zinc-900 font-semibold text-xs">Licensed &amp; Insured</p>
                  <p className="text-zinc-500 text-xs">Indiana &amp; Kentucky</p>
                </div>
              </div>
            </div>

            {/* Second image row */}
            <div className="mt-10 rounded-3xl overflow-hidden h-48">
              <img
                src="https://readdy.ai/api/search-image?query=premium%20solar%20panel%20array%20on%20modern%20luxury%20american%20home%20roof%20close%20up%20architectural%20detail%20blue%20sky%20white%20clouds%20warm%20sunlight%20bokeh%20background&width=700&height=300&seq=why02&orientation=landscape"
                alt="Premium solar installation detail"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>

          {/* Right — Benefits */}
          <div
            className={`transition-all duration-700 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="space-y-5 mt-12">
              {items.map((item, i) => (
                <div
                  key={item.title}
                  className={`flex gap-5 bg-white rounded-3xl p-6 border border-stone-100 hover:border-amber-200 transition-all duration-700 hover:-translate-y-0.5 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                  style={{ transitionDelay: `${400 + i * 80}ms` }}
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-amber-100 to-amber-50 rounded-2xl flex-shrink-0 border border-amber-100">
                    <i className={`${item.icon} text-amber-600 text-2xl`} />
                  </div>
                  <div className="pt-1">
                    <h3 className="font-semibold text-zinc-900 text-base mb-1.5">{item.title}</h3>
                    <p className="text-zinc-500 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
